import Container from "../../../components/element/container";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { pdf } from "@react-pdf/renderer";
import Icon from "../../../components/element/icon";
import VehicleSalesOrderPdf, {
  createVehicleSalesOrderData,
} from "../../../components/pdf/vehicle.sales.order.pdf";
import BuyerSalePurchaseAgreementPdf from "../../../components/pdf/buyer.sales.purchase.agreement.pdf";
import agreementData from "../../../components/pdf/agreement.data";
import SignatureUpload from "./signature.upload";
import useMediaUrl from "../../../components/hook/use.media.url";

const PREVIEW_DELAY = 400;

export default function VsoSigning({
  purchase = {},
  isSubmitting,
  onComplete,
  readOnly = false,
  initialData = {},
  document: submittedDocument,
  signatureDocument,
  documentType = "vso",
  documentOnly = false,
}) {
  const submittedUrl = useMediaUrl(submittedDocument);
  const { t } = useTranslation();
  const { profileInfo } = useSelector((state) => state.profile || {});
  const isAgreement = documentType === "spa";
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const previousPointRef = useRef(null);
  const hasInkRef = useRef(false);
  const previewUrlRef = useRef("");
  const [signature, setSignature] = useState("");
  const [uploadedSignature, setUploadedSignature] = useState("");
  const [uploadedSignatureFile, setUploadedSignatureFile] = useState(null);
  const [signatureUploadLoading, setSignatureUploadLoading] = useState(false);
  const [generatedPreviewUrl, setPreviewUrl] = useState("");
  const previewUrl = readOnly ? submittedUrl : generatedPreviewUrl;
  const [previewLoading, setPreviewLoading] = useState(true);
  const [isPreparingDocument, setIsPreparingDocument] = useState(false);

  const baseData = useMemo(
    () =>
      isAgreement
        ? agreementData(purchase, profileInfo)
        : createVehicleSalesOrderData(purchase),
    [isAgreement, profileInfo, purchase],
  );

  const signerName = isAgreement
    ? purchase.source?.identity_details?.company_name ||
      `${purchase.source?.identity_details?.legal_last_name ?? ""} ${purchase.source?.identity_details?.legal_first_name ?? ""}`
    : baseData.customer?.name === "-" ? "" : baseData.customer?.name;
  const [fullName, setFullName] = useState(
    readOnly ? initialData.full_name || "" : signerName || "",
  );
  const [documentDate] = useState(
    readOnly ? initialData.date || "" : baseData.date || "",
  );
  const reference = purchase.reservationId || purchase.orderId || "document";
  const fileName =
    (readOnly && submittedDocument?.name) ||
    (isAgreement ? `SPA-${reference}.pdf` : `VSO-${reference}.pdf`);
  const [combinedSignature, setCombinedSignature] = useState("");

  // Every signature goes through the same canvas, so a drawn signature is the
  // same size whether or not an image was uploaded with it.
  useEffect(() => {
    if (!signature && !uploadedSignature) {
      setCombinedSignature("");
      return undefined;
    }

    let cancelled = false;
    setCombinedSignature("");
    const drawn = new Image();
    const uploaded = new Image();
    const ready = (image, src) =>
      !src || (image.complete && image.naturalWidth);
    const compose = () => {
      if (!ready(drawn, signature) || !ready(uploaded, uploadedSignature))
        return;
      const canvas = document.createElement("canvas");
      canvas.width = 1000;
      canvas.height = 380;
      const context = canvas.getContext("2d");
      // The two signatures overlap rather than sitting side by side: the
      // uploaded image is the larger one underneath, with the drawn signature
      // layered on top of it.
      const draw = (image, boxWidth, boxHeight) => {
        const scale = Math.min(
          boxWidth / image.naturalWidth,
          boxHeight / image.naturalHeight,
        );
        const width = image.naturalWidth * scale;
        const height = image.naturalHeight * scale;
        context.drawImage(
          image,
          (canvas.width - width) / 2,
          (canvas.height - height) / 2,
          width,
          height,
        );
      };
      if (uploadedSignature)
        draw(uploaded, canvas.width * 0.96, canvas.height * 0.96);
      if (signature) draw(drawn, canvas.width * 0.6, canvas.height * 0.6);
      if (!cancelled) setCombinedSignature(canvas.toDataURL("image/png"));
    };
    drawn.onload = compose;
    uploaded.onload = compose;
    if (signature) drawn.src = signature;
    if (uploadedSignature) uploaded.src = uploadedSignature;
    compose();
    return () => {
      cancelled = true;
    };
  }, [signature, uploadedSignature]);

  const pdfData = useMemo(() => {
    const signatureImage = combinedSignature;

    if (isAgreement) {
      return {
        ...baseData,
        date: documentDate,
        buyer: { ...baseData.buyer, name: fullName },
        buyerSignature: signatureImage,
      };
    }

    return {
      ...baseData,
      date: documentDate,
      customer: { ...baseData.customer, name: fullName },
      customerSignature: signatureImage,
    };
  }, [baseData, combinedSignature, documentDate, fullName, isAgreement]);

  const DocumentPdf = isAgreement
    ? BuyerSalePurchaseAgreementPdf
    : VehicleSalesOrderPdf;

  useEffect(() => {
    if (readOnly) return undefined;
    let cancelled = false;
    const timeout = window.setTimeout(
      async () => {
        setPreviewLoading(true);

        try {
          const blob = await pdf(
            <DocumentPdf data={pdfData} fileName={fileName} />,
          ).toBlob();

          if (cancelled) return;

          const nextUrl = URL.createObjectURL(blob);
          const previousUrl = previewUrlRef.current;
          previewUrlRef.current = nextUrl;
          setPreviewUrl(nextUrl);

          if (previousUrl) URL.revokeObjectURL(previousUrl);
        } catch {
          if (!cancelled) setPreviewLoading(false);
        }
      },
      previewUrlRef.current ? PREVIEW_DELAY : 0,
    );

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [DocumentPdf, fileName, pdfData, readOnly]);

  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    [],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const resizeCanvas = () => {
      const bounds = canvas.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const previousImage = hasInkRef.current
        ? canvas.toDataURL("image/png")
        : "";
      const nextWidth = Math.max(1, Math.round(bounds.width * ratio));
      const nextHeight = Math.max(1, Math.round(bounds.height * ratio));

      if (canvas.width === nextWidth && canvas.height === nextHeight) return;

      canvas.width = nextWidth;
      canvas.height = nextHeight;

      const context = canvas.getContext("2d");
      context.scale(ratio, ratio);
      context.lineCap = "round";
      context.lineJoin = "round";
      context.lineWidth = 2.25;
      context.strokeStyle = "#101820";

      if (previousImage) {
        const image = new Image();
        image.onload = () =>
          context.drawImage(image, 0, 0, bounds.width, bounds.height);
        image.src = previousImage;
      }
    };

    resizeCanvas();
    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(canvas);

    return () => observer.disconnect();
  }, []);

  const getPoint = (event) => {
    const bounds = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    const signatureLineTop = bounds.height - 48;

    if (x < 0 || x > bounds.width || y < 0 || y > signatureLineTop) {
      return null;
    }

    return { x, y };
  };

  const startDrawing = (event) => {
    const point = getPoint(event);
    if (!point) return;

    event.preventDefault();
    canvasRef.current.setPointerCapture(event.pointerId);
    drawingRef.current = true;
    previousPointRef.current = point;
  };

  const draw = (event) => {
    if (!drawingRef.current) return;

    event.preventDefault();
    const context = canvasRef.current.getContext("2d");
    const pointerEvents = event.nativeEvent.getCoalescedEvents?.() || [event];

    pointerEvents.forEach((pointerEvent) => {
      const point = getPoint(pointerEvent);
      const previousPoint = previousPointRef.current;

      if (!point) {
        previousPointRef.current = null;
        return;
      }

      if (previousPoint) {
        context.beginPath();
        context.moveTo(previousPoint.x, previousPoint.y);
        context.lineTo(point.x, point.y);
        context.stroke();
        hasInkRef.current = true;
      }

      previousPointRef.current = point;
    });
  };

  // The pad is much larger than the signature box in the document, so the
  // exported image is cropped to the ink. Without this the PDF scales the whole
  // canvas, including the empty area, and the signature lands off the line.
  const exportSignature = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");
    const { width, height } = canvas;
    const { data } = context.getImageData(0, 0, width, height);
    let minX = width;
    let minY = height;
    let maxX = -1;
    let maxY = -1;

    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        if (data[(y * width + x) * 4 + 3] > 8) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < 0) return "";

    const padding = Math.round(Math.max(width, height) * 0.02);
    const left = Math.max(0, minX - padding);
    const top = Math.max(0, minY - padding);
    const cropWidth = Math.min(width, maxX + padding + 1) - left;
    const cropHeight = Math.min(height, maxY + padding + 1) - top;
    const cropped = document.createElement("canvas");

    cropped.width = cropWidth;
    cropped.height = cropHeight;
    cropped
      .getContext("2d")
      .drawImage(
        canvas,
        left,
        top,
        cropWidth,
        cropHeight,
        0,
        0,
        cropWidth,
        cropHeight,
      );

    return cropped.toDataURL("image/png");
  };

  const stopDrawing = (event) => {
    if (!drawingRef.current) return;

    drawingRef.current = false;
    previousPointRef.current = null;
    if (canvasRef.current.hasPointerCapture(event.pointerId)) {
      canvasRef.current.releasePointerCapture(event.pointerId);
    }
    if (hasInkRef.current) {
      setSignature(exportSignature());
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    context.clearRect(0, 0, canvas.width, canvas.height);
    hasInkRef.current = false;
    setSignature("");
  };

  // A stored file sits on another origin, where the download attribute is
  // ignored and the browser just opens the PDF. Fetching it first and saving
  // the blob keeps the icon a real download.
  const downloadDocument = async (event) => {
    if (!previewUrl || previewUrl.startsWith("blob:")) return;

    event.preventDefault();

    try {
      const response = await fetch(previewUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = objectUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      // Blocked by the file host, so fall back to opening it.
      window.open(previewUrl, "_blank", "noopener,noreferrer");
    }
  };

  const completeSigning = async () => {
    if (readOnly) return;
    setIsPreparingDocument(true);

    try {
      const blob = await pdf(
        <DocumentPdf data={pdfData} fileName={fileName} />,
      ).toBlob();
      const signedDocument = new File([blob], fileName, {
        type: "application/pdf",
      });

      await onComplete(signedDocument, {
        full_name: fullName,
        date: documentDate,
        signature: pdfData.customerSignature,
        signature_image: uploadedSignatureFile,
      });
    } catch {
      // The parent reports API failures; keep the signing form available.
    } finally {
      setIsPreparingDocument(false);
    }
  };

  const submitting = isSubmitting || isPreparingDocument;

  return (
    <section
      className={`vso-signing ${documentOnly ? "vso-document-only" : ""}`}
      aria-label={
        readOnly
          ? `Review ${isAgreement ? "sale and purchase agreement" : "vehicle sales order"}`
          : `Sign ${isAgreement ? "sale and purchase agreement" : "vehicle sales order"}`
      }
    >
      <article className="vso-document-panel">
        <header className="vso-panel-heading">
          {/* The step number only makes sense while the signature panel is
              still there to be step 2. */}
          {!documentOnly && <span>1</span>}
          <Container tag="div">
            <h3>
              {documentOnly
                ? isAgreement
                  ? t("Signed Sale and Purchase Agreement")
                  : t("Signed Vehicle Sales Order")
                : t("Document Preview")}
            </h3>
            <p>
              {documentOnly
                ? t(
                    "You have signed this document. It is saved with your order.",
                  )
                : isAgreement
                  ? t("Buyer Sale and Purchase Agreement")
                  : t("Vehicle Sales Order (VSO)")}
            </p>
          </Container>
        </header>

        <Container tag="div" className="vso-document-frame">
          <Container tag="div" className="vso-document-toolbar">
            <span className="vso-document-name">{fileName}</span>
            <Container tag="div" className="vso-document-controls">
              <a
                href={previewUrl || undefined}
                download={fileName}
                onClick={downloadDocument}
                aria-label={
                  isAgreement
                    ? "Download sale and purchase agreement"
                    : "Download vehicle sales order"
                }
              >
                <Icon icon="solar:download-minimalistic-linear" />
              </a>
            </Container>
          </Container>
          {previewUrl && (
            <iframe
              // The viewer scrolls through every page on its own and keeps
              // pinch zoom, so it is not remounted per page.
              key={previewUrl}
              src={`${previewUrl}#toolbar=0&navpanes=0&statusbar=0&messages=0`}
              title={fileName}
              onLoad={() => setPreviewLoading(false)}
            />
          )}
          {readOnly && !previewUrl && (
            <p role="status">
              {t("The signed VSO is not available in this order yet.")}
            </p>
          )}
          {!readOnly && previewLoading && (
            <Container tag="div" className="vso-preview-loading">
              {t("Updating preview...")}
            </Container>
          )}
        </Container>
        {/* {readOnly && submittedDocument && <SubmittedMedia document={submittedDocument} icon="solar:document-text-linear" />} */}
      </article>

      {!documentOnly && (
        <article className="vso-signature-panel">
          <header className="vso-panel-heading">
            <span>2</span>
            <Container tag="div">
              <h3>{t("Provide Your Signature")}</h3>
              <p>
                {t("Please review the document and confirm your signature.")}
              </p>
            </Container>
          </header>

          <Container tag="div" className="vso-signer-fields">
            <label>
              <span>
                {t("Full Name (as per IC/Passport)")}
                <i className="vso-required">*</i>
              </span>
              <input
                readOnly={readOnly}
                placeholder={readOnly ? "Not provided" : undefined}
                type="text"
                value={fullName}
                onChange={(event) => setFullName(event.target.value)}
              />
            </label>
            <label>
              <span>{t("Date")}</span>
              <Container tag="div" className="vso-date-field">
                <Icon icon="solar:calendar-linear" />
                <input
                  type="text"
                  value={documentDate}
                  placeholder={readOnly ? "Not provided" : undefined}
                  disabled
                  aria-disabled="true"
                />
              </Container>
            </label>
          </Container>

          <Container tag="div" className="vso-signature-heading">
            <Container tag="div">
              <span>
                {t("Signature")}
                <i className="vso-required">*</i>
              </span>
              <small>
                {readOnly
                  ? t("Submitted · View only")
                  : t("Sign in the box below")}
              </small>
            </Container>
            {!readOnly && (
              <button
                type="button"
                className="vso-clear-signature"
                onClick={clearSignature}
              >
                <Icon icon="solar:restart-linear" />
                {t("Clear")}
              </button>
            )}
          </Container>

          <Container tag="div" className="vso-signature-pad">
            {readOnly ? (
              initialData.signature ? (
                <img
                  className="vso-submitted-signature"
                  src={initialData.signature}
                  alt="Submitted signature"
                />
              ) : (
                <p className="vso-submitted-signature-note">
                  {t("View your signature in the signed VSO.")}
                </p>
              )
            ) : (
              <canvas
                ref={canvasRef}
                onPointerDown={startDrawing}
                onPointerMove={draw}
                onPointerUp={stopDrawing}
                onPointerCancel={stopDrawing}
                aria-label="Signature drawing area"
              />
            )}
            {!readOnly && <span>{t("Sign above the line")}</span>}
          </Container>

          <Container tag="div" className="vso-signature-or" aria-hidden="true">
            <span />
            <small>{t("OR")}</small>
            <span />
          </Container>

          <SignatureUpload
            readOnly={readOnly}
            document={signatureDocument}
            value={uploadedSignature}
            onChange={(value, file) => {
              setUploadedSignature(value);
              setUploadedSignatureFile(file || null);
            }}
            disabled={submitting}
            onLoadingChange={setSignatureUploadLoading}
          />

          <p className="vso-signing-confirmation">
            <Icon icon="solar:info-circle-linear" />
            {t(
              "By signing above, I confirm that I have read and understood the contents of this document and agree to proceed.",
            )}
          </p>

          {!readOnly && (
            <Container tag="div" className="vso-signing-actions">

              <button
                type="button"
                disabled={
                  !(signature || uploadedSignature) ||
                  !combinedSignature ||
                  !fullName.trim() ||
                  signatureUploadLoading ||
                  submitting
                }
                onClick={completeSigning}
              >
                {submitting ? t("Submitting...") : t("Complete Signing")}
                <Icon icon="solar:arrow-right-linear" />
              </button>
            </Container>
          )}
        </article>
      )}

      {!documentOnly && (
        <p className="vso-security-note security-note-inline">
          <Icon
            tag="span"
            icon="solar:lock-keyhole-linear"
            className="security-lock"
          />
          {t("Your signature is secure and legally binding.")}
        </p>
      )}
    </section>
  );
}
